import type { BanDich } from '../../types';
import type { viVanHanh } from './van-hanh.vi';

export const enVanHanh: Pick<BanDich, keyof typeof viVanHanh> = {
  troLyAiVanHanhTongQuan: {
    tieuDe: 'AI Assistant overview',
    mo: 'Q&A activity over the last {{n}} days.',
    xuHuong: 'Question trend',
    xuHuongMo: 'Questions per day. Days with no questions still show, so gaps are visible.',
    xuHuongGioMo: 'Total questions per hour over {{n}} days (Vietnam time) — shows peak hours.',
    theoNgay: 'By day',
    theoGio: 'By hour',
    cheDoXuHuong: 'Show trend by',
    chuaCoLuot: 'No questions in this period yet.',
    cauThuongGap: 'Frequent questions',
    cauThuongGapMo: 'Similar phrasings are grouped into one row.',
    chuaCoCau: 'No questions recorded yet.',
    goiYNgay: '{{ngay}} · {{n}} questions',
    goiYGio: '{{tu}}:00–{{den}}:00 · {{n}} questions',
    nhanGio: '{{gio}}h',
    thu0: 'Sun',
    thu1: 'Mon',
    thu2: 'Tue',
    thu3: 'Wed',
    thu4: 'Thu',
    thu5: 'Fri',
    thu6: 'Sat',
  },
  troLyAiVanHanhSuDung: {
    chuaLayDuoc: "Couldn't load usage figures. Try reloading the page.",
    luotHoi: 'Questions',
    trongKy: 'In the last {{n}} days',
    traLoiDuoc: 'Answered',
    tyLe: '{{p}} of all questions',
    biTuChoi: 'Refused',
    biTuChoiMo: 'Outside the permitted scope',
    gapLoi: 'Failed',
    khongCoLoi: 'No failed questions',
    gapLoiMo: 'Reading the data failed, so no answer was given',
    tocDo: 'Response time',
    thuong: 'Typical',
    chamNhat: 'Slowest',
    tocDoMo: "Time spent fetching the data for an answer. Doesn't include the time the assistant spends writing the answer.",
    ms: '{{so}} ms',
    giay: '{{so}} s',
    theoNguon: 'Sources asked about',
    theoNguonMo: 'Which sources people ask about most.',
    chuaTraLoiDuoc: '{{n}} not answered',
    chuaCoNguon: 'No source was asked about in this period.',
    theoVai: "Who's asking",
    theoVaiMo: 'Questions by user role.',
    chuaCoVai: 'No one asked a question in this period.',
    hoiThoaiDaLuu: 'Saved conversations',
    hoiThoaiMo: 'Questions in this period, split by AI Q&A and each chatbot.',
    troLyAi: 'AI Assistant',
    chatbot: 'Chatbot',
    doanLuot: '{{doan}} conversations · {{luot}} questions',
    chuaCoDoan: 'No conversations yet.',
  },
  troLyAiVanHanhNhatKy: {
    tieuDe: 'Audit log',
    phuDe:
      'Records every sync cycle and every SQL query that went through query control, including refused ones — the place to look when you need to know why the assistant couldn’t answer a question.',
    taiLai: 'Reload',
    tabCauHoi: 'Questions & SQL ({{n}})',
    tabDongBo: 'Sync cycles ({{n}})',
    tabTroChuyen: 'Conversation history',
    colLuc: 'Time',
    colBang: 'Table',
    colKetQua: 'Result',
    colDong: 'Rows',
    colGhiChu: 'Note',
    colCauHoi: 'Question',
    colChiTiet: 'Query / refusal reason',
    chepDayDu: 'full sync',
    chepTiep: 'incremental sync',
    boQua: 'skipped',
    dungLai: 'stopped midway',
    biTuChoi: 'refused',
    chayHong: 'failed to run',
    soDong: '{{n}} rows',
    chayTrucTiep: '— run directly —',
    cauLenhTrucTiep: 'Query run directly',
    docGhi: 'read {{doc}} · wrote {{ghi}}',
    xoaThem: ' · deleted {{n}}',
    giayNgan: '{{so}} s',
    rongCauHoi: 'Nothing yet. Ask a question in AI Q&A, then come back.',
    rongDongBo: 'No sync cycles recorded yet. Click Sync in Source connections.',
    congTuChoi: 'Refusal reasons',
    cauLenh: 'Query',
    bangDaDoc: 'Tables read',
    tieuDeCauHoi: 'Questions & SQL run',
    tieuDeDongBo: 'Sync cycles',
    xemLuot: 'View run details',
    xemLuotCua: 'View details of the run at {{luc}}',
    chiTietMo: '{{luc}} · {{nguon}}',
    chiTietMs: ' · {{ms}} ms',
    locTatCa: 'All',
    timTroChuyen: 'Search by conversation title or content...',
    loaiTroLy: 'AI Assistant',
    loaiChatbot: 'Chatbot',
    locLoai: 'Conversation type',
    khoangNgay: 'Time range',
    trongNgay: 'Last {{n}} days',
    soLuot: '{{n}} messages',
    doanChuaDatTen: '(untitled)',
    khachNhung: 'Guest (embedded widget)',
    xemCauLenh: 'View the query that ran',
    maTraCuu: 'Processing trace ID',
    chepMa: 'Copy trace ID',
    daChepMa: 'Copied trace ID.',
    moTuNhatKy: 'Opened from the activity log',
    chonDoan: 'Select a conversation',
    chonDoanMo: 'The full content — questions, answers and the queries that ran — shows here.',
    moDoan: '{{nhan}} · {{vai}} · started {{luc}}',
    rongTroChuyen: 'No conversations',
    rongTroChuyenKhongLoc: 'No conversations were saved in this period.',
    chiQuanTri: 'Only admins can view conversation history',
    chiQuanTriMo: "History across all roles may contain data your role isn't allowed to see.",
    danhSachDoan: 'Conversation list',
  },
  troLyAiVanHanhDem: {
    tieuDe: 'Answer cache',
    moTa: 'A question identical to one already asked is answered straight from the cache, without recomputing.',
    bat: 'Enable cache',
    sucChua: 'Maximum size (questions)',
    khoang: 'Between {{thap}} and {{cao}}',
    luu: 'Save cache settings',
    daLuu: 'Saved cache settings.',
    khongTaiDuoc: "Couldn't read the cache settings",
    daKep: 'The server adjusted the values to fit its limits — the fields show what the server accepted.',
    soMuc: 'Cached: {{n}} questions',
    tiLeTrung: 'Reuse rate (cache hit): {{p}}%',
    nguonEnv:
      "Redis isn't configured for AI Assistant — settings come from environment variables and changes here won't take effect.",
    hanPhut: 'Answer cache lifetime (minutes)',
    khoangPhut: '{{thap}}–{{cao}} minutes',
    demTrucTiep: 'Also apply to direct-read sources',
    demTrucTiepMo:
      'Off by default — answers from direct-read sources always reflect the data at question time. Turn on only when the database changes a few times a day.',
    sqlHanPhut: 'Query cache lifetime (minutes)',
    sqlTat:
      '0 = off. When an answer expires, the system reuses the old query and reruns it for fresh figures — skipping SQL generation.',
    tiengHanPhut: 'Voice cache lifetime (minutes)',
    tiengTat:
      '0 = don’t cache voiced answers. Keep it shorter than the answer lifetime, since a voiced answer takes about twenty times the space.',
    tiengCanRedis: 'Needs Redis — without it, voiced answers are never cached whatever value you set.',
    demTieng: 'Voice caching: {{gt}}',
    co: 'on',
    khong: 'off',
    cheDoRedis: 'Shared cache (Redis)',
    cheDoTienTrinh: 'In-process cache',
  },
  troLyAiVanHanhThuNghiem: {
    tieuDe: 'Testing',
    phuDe: 'Check two things separately: what context the model receives, and whether an SQL query is allowed to run.',
    chonNguon: 'Data source to test',
    loiNguonTieuDe: "Couldn't read the source list",
    loiNguonGoiY:
      "If you signed in before your latest permission change, your session hasn't picked up the new permissions — sign out and sign in again.",
    chuaCoNguonSanSang: 'No source is ready yet. Connect a source in Source connections, then come back.',
    sangKetNoi: 'Go to Source connections',
    tabCauLenh: 'Query',
    tabNguCanh: 'Context pack',
    cauHoiTuyChon: 'Question (optional — only recorded in the log)',
    viDuCauHoi: 'E.g. How much was lost last month?',
    cauLenhSql: 'SQL query',
    chayQuaCong: 'Check and run',
    tranDong: '{{dialect}} · limit {{n}} rows',
    cauMauDaLuu: 'Saved sample queries — click to load',
    congTuChoi: 'Query refused',
    cauDaVietLai: 'The query that actually ran — query control adjusted it',
    soDong: '{{n}} rows',
    daCatTran: 'cut at the row limit',
    soLieuTrucTiep: 'live data',
    duLieuToi: 'data updated to {{luc}}',
    hien100: 'Showing the first 100 of {{n}} rows.',
    ketQuaHienODay: 'Results show here, along with the query that actually ran if query control adjusted it.',
    bangKetQua: 'Query results',
    cauHoiCatNguCanh: 'Question (optional — the context is trimmed to this question)',
    deTrongXemToanBo: 'Leave empty to see the whole pack',
    soBang: '{{n}} tables',
    soLuat: '{{n}} mandatory rules',
    soThuatNgu: '{{n}} glossary terms',
    soCauMau: '{{n}} sample queries',
    soKyTu: '{{n}} characters',
    noiDungGoi: 'Context pack content',
    ketQua: 'Result',
    tomTatGoi: 'Pack summary',
    luuLamMau: 'Save as sample query',
    daLuuMau: 'Saved the sample query — the assistant will refer to it when answering similar questions.',
    mauCanCauHoi: 'Fill in the Question field to save a sample query',
  },
};
